// Advanced video background categories with curated images

export interface BackgroundImage {
  id: string
  url: string
  thumbnail: string
  category: string
  name: string
  /**
   * Blur radius in pixels when this entry means "blur my real background"
   * rather than "replace it". Mutually exclusive with a usable `url` — the two
   * are alternative ways to hide the same room, so one selection covers both
   * and the picker cannot end up in a state where both are on.
   */
  blurPx?: number
}

/** The selection that means "blur my own background", at a given strength. */
export function blurBackground(blurPx: number, name: string): BackgroundImage {
  return {
    id: `blur-${blurPx}`,
    url: '',
    thumbnail: '',
    category: 'blur',
    name,
    blurPx,
  }
}

export interface BackgroundCategory {
  id: string
  name: string
  icon: string
  backgrounds: BackgroundImage[]
}

export const backgroundCategories: BackgroundCategory[] = [
  {
    id: "office",
    name: "Office",
    icon: "🏢",
    backgrounds: [
      {
        id: "office-1",
        url: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80",
        category: "office",
        name: "Modern Office"
      },
      {
        id: "office-2",
        url: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=400&q=80",
        category: "office",
        name: "Bright Workspace"
      },
      {
        id: "office-3",
        url: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=400&q=80",
        category: "office",
        name: "Conference Room"
      },
      {
        id: "office-4",
        url: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400&q=80",
        category: "office",
        name: "Executive Office"
      },
      {
        id: "office-5",
        url: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=400&q=80",
        category: "office",
        name: "Coworking Space"
      }
    ]
  },
  {
    id: "nature",
    name: "Nature",
    icon: "🌿",
    backgrounds: [
      {
        id: "nature-1",
        url: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=80",
        category: "nature",
        name: "Forest Path"
      },
      {
        id: "nature-2",
        url: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=400&q=80",
        category: "nature",
        name: "Mountain Lake"
      },
      {
        id: "nature-3",
        url: "https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=400&q=80",
        category: "nature",
        name: "Green Valley"
      },
      {
        id: "nature-4",
        url: "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=400&q=80",
        category: "nature",
        name: "Tropical Beach"
      },
      {
        id: "nature-5",
        url: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&q=80",
        category: "nature",
        name: "Sunset Landscape"
      }
    ]
  },
  {
    id: "space",
    name: "Space",
    icon: "🚀",
    backgrounds: [
      {
        id: "space-1",
        url: "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=400&q=80",
        category: "space",
        name: "Milky Way"
      },
      {
        id: "space-2",
        url: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&q=80",
        category: "space",
        name: "Starry Night"
      },
      {
        id: "space-3",
        url: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400&q=80",
        category: "space",
        name: "Galaxy"
      },
      {
        id: "space-4",
        url: "https://images.unsplash.com/photo-1464802686167-b939a6910659?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1464802686167-b939a6910659?w=400&q=80",
        category: "space",
        name: "Northern Lights"
      },
      {
        id: "space-5",
        url: "https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3?w=400&q=80",
        category: "space",
        name: "Nebula"
      }
    ]
  },
  {
    id: "abstract",
    name: "Abstract",
    icon: "🎨",
    backgrounds: [
      {
        id: "abstract-1",
        url: "https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=400&q=80",
        category: "abstract",
        name: "Gradient Flow"
      },
      {
        id: "abstract-2",
        url: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=400&q=80",
        category: "abstract",
        name: "Color Waves"
      },
      {
        id: "abstract-3",
        url: "https://images.unsplash.com/photo-1550859492-d5da9d8e45f3?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1550859492-d5da9d8e45f3?w=400&q=80",
        category: "abstract",
        name: "Geometric Pattern"
      },
      {
        id: "abstract-4",
        url: "https://images.unsplash.com/photo-1567095761054-7a02e69e5c43?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1567095761054-7a02e69e5c43?w=400&q=80",
        category: "abstract",
        name: "Liquid Art"
      },
      {
        id: "abstract-5",
        url: "https://images.unsplash.com/photo-1553356084-58ef4a67b2a7?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1553356084-58ef4a67b2a7?w=400&q=80",
        category: "abstract",
        name: "Digital Art"
      }
    ]
  },
  {
    id: "urban",
    name: "Urban",
    icon: "🏙️",
    backgrounds: [
      {
        id: "urban-1",
        url: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&q=80",
        category: "urban",
        name: "City Skyline"
      },
      {
        id: "urban-2",
        url: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&q=80",
        category: "urban",
        name: "Modern Architecture"
      },
      {
        id: "urban-3",
        url: "https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400&q=80",
        category: "urban",
        name: "Night City"
      },
      {
        id: "urban-4",
        url: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=400&q=80",
        category: "urban",
        name: "Urban Street"
      },
      {
        id: "urban-5",
        url: "https://images.unsplash.com/photo-1496568816309-51d7c20e3b21?w=1920&q=80",
        thumbnail: "https://images.unsplash.com/photo-1496568816309-51d7c20e3b21?w=400&q=80",
        category: "urban",
        name: "Downtown"
      }
    ]
  },
  {
    id: "solid",
    name: "Solid Colors",
    icon: "🎨",
    backgrounds: [
      {
        id: "solid-blue",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%233b82f6'/%3E%3C/svg%3E",
        thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%233b82f6'/%3E%3C/svg%3E",
        category: "solid",
        name: "Blue"
      },
      {
        id: "solid-green",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%2310b981'/%3E%3C/svg%3E",
        thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%2310b981'/%3E%3C/svg%3E",
        category: "solid",
        name: "Green"
      },
      {
        id: "solid-purple",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%238b5cf6'/%3E%3C/svg%3E",
        thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%238b5cf6'/%3E%3C/svg%3E",
        category: "solid",
        name: "Purple"
      },
      {
        id: "solid-orange",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%23f97316'/%3E%3C/svg%3E",
        thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f97316'/%3E%3C/svg%3E",
        category: "solid",
        name: "Orange"
      },
      {
        id: "solid-pink",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%23ec4899'/%3E%3C/svg%3E",
        thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23ec4899'/%3E%3C/svg%3E",
        category: "solid",
        name: "Pink"
      }
    ]
  }
]

export const getAllBackgrounds = (): BackgroundImage[] => {
  return backgroundCategories.flatMap(category => category.backgrounds)
}

export const getBackgroundsByCategory = (categoryId: string): BackgroundImage[] => {
  const category = backgroundCategories.find(cat => cat.id === categoryId)
  return category?.backgrounds || []
}

export const getBackgroundById = (id: string): BackgroundImage | undefined => {
  return getAllBackgrounds().find(bg => bg.id === id)
}
